import React from 'react'

import { isEqual } from 'lodash'

import { Spin } from '@emmacare/common/design-system/components/Spin'

import UnexpectedError from 'src/components/UnexpectedError'

// The design-system Spin fills its parent (`height: 100%`) and centers itself, so
// it needs a parent with a resolvable height. A flex container gives the Spin
// wrapper a stretched, definite height, which it can then center within.
const DEFAULT_LOADER_STYLE = {
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  minHeight: '60vh'
}

const withAsyncLoader = (config) => (Component) =>
  class WithAsyncLoader extends React.Component {
    static displayName = `WithAsyncLoader(${
      Component.displayName || Component.name || 'Component'
    })`

    constructor(props) {
      super(props)

      const { load, ...restParams } = config

      if (typeof load !== 'function') {
        throw new TypeError('Parameter "load" is not a function')
      }

      this.config = {
        propagateAs: 'data',
        loader: (
          <div style={DEFAULT_LOADER_STYLE}>
            <Spin size="large" />
          </div>
        ),
        load: this.promisifyLoadFunction(load),
        ...restParams
      }

      this.state = {
        isProcessed: false,
        isProcessing: false,
        res: null,
        err: null
      }
    }

    componentDidMount = () => {
      this.executeLoadFunction()
    }

    componentDidUpdate(prevProps) {
      const { shouldComponentReloadOnUpdate } = this.config
      const shouldReloadOnUpdate =
        typeof shouldComponentReloadOnUpdate === 'function'
          ? shouldComponentReloadOnUpdate(prevProps, this.props)
          : Boolean(shouldComponentReloadOnUpdate)

      if (shouldReloadOnUpdate && !isEqual(this.props, prevProps)) {
        this.executeLoadFunction()
      }
    }

    executeLoadFunction = () => {
      const { load } = this.config

      load(this.props).then(
        (res) => this.setState({ res }),
        (err) => this.setState({ err })
      )
    }

    promisifyLoadFunction = (loadFunction) => (props) =>
      new Promise((resolve, reject) => {
        this.setState({
          isProcessing: true,
          isProcessed: false
        })
        let results = loadFunction(props)

        if (!(results instanceof Promise)) {
          results = Promise.resolve(results)
        }

        results
          .then((res) => resolve(res))
          .catch((err) => reject(err))
          .then(() => {
            this.setState({
              isProcessing: false,
              isProcessed: true
            })
          })
      })

    render = () => {
      const { loader, propagateAs } = this.config
      const { isProcessing, isProcessed, res, err } = this.state

      if (!isProcessed || isProcessing) return loader

      if (err) {
        return <UnexpectedError />
      }

      const propagatedProps = {
        [propagateAs]: res
      }

      return <Component {...this.props} {...propagatedProps} />
    }
  }

export default withAsyncLoader
